import { createGlobalState } from '@vueuse/core';
import { computed, inject, nextTick, ref } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import { useToasted } from '~/composables/useToasted';
import { contactsClient, groupContactsClient, scheduledMessagesClient } from '~/services';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import axios from '~/services/axios';
import { processWithConcurrency } from '~/utils/async';
import { VxSwal } from '@voxie/frontend-components';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { useDirtyRef } from '~/composables/useDirtyRef';

const MAX_CONCURRENT_REQUESTS = 4;

const PROFILE_FIELDS = ['email', 'entry_channel', 'membership_status', 'lifecycle_status'];

const defaultContact = (defaultData = {}) => {
    return {
        id: undefined,
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        entry_point: '',
        time_zone: '',
        created_at: undefined,
        ...defaultData,
    };
};

const emptyProfile = () => ({
    email: null,
    entry_channel: null,
    membership_status: null,
    lifecycle_status: null,
    custom_attributes: [],
    external_ids: {},
});

/**
 * @param {{ key: string, value: string }[]} original
 * @param {{ key: string, value: string }[]} current
 */
const diffCustomAttributes = (original, current) => ({
    upserts: current.filter((attribute) => {
        const originalAttribute = original.find((a) => a.key === attribute.key);
        return !originalAttribute || originalAttribute.value !== attribute.value;
    }),
    deletes: original.filter((attribute) => !current.some((a) => a.key === attribute.key)),
});

const profileWrite = (profile, original) => {
    const write = {};

    for (const field of PROFILE_FIELDS) {
        if (profile[field] !== original[field]) {
            write[field] = profile[field];
        }
    }

    const { upserts, deletes } = diffCustomAttributes(original.custom_attributes, profile.custom_attributes);

    if (upserts.length) {
        write.custom_attributes = upserts.map(({ key, value }) => ({ key, value }));
    }

    if (deletes.length) {
        write.custom_attributes_to_delete = deletes.map((attribute) => attribute.key);
    }

    return Object.keys(write).length ? write : null;
};

export const useContactDetails = createGlobalState(() => {
    const FEATURES = inject('FEATURES');

    // used by the message hub as in-memory cache to render the screen immediately when available
    const contactsBucket = ref({});

    const toasted = useToasted();
    const teamId = Spark.state.currentTeam.id;
    const groupId = ref();
    const { currentGroup } = useCurrentGroup();

    const catchContactError = (error) => {
        if (axios.isCancel(error)) {
            return;
        }

        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status == HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.', {
                sticky: true,
            });
        }
    };

    const isCached = (contactId) => {
        return Boolean(contactsBucket.value[contactId]);
    };

    const prefill = (contactId, prefillGroupId, defaultData) => {
        if (!contactId) {
            groupId.value = prefillGroupId;
            contact.value = defaultContact();
            resetDirty();
            if (FEATURES.local_attributes && FEATURES.contact_groups) {
                seedAttributeSets();
            }
            return;
        }

        contactId = Number(contactId);

        if (contact.value.id === contactId) {
            groupId.value = prefillGroupId;
            if (defaultData) {
                contactsBucket.value[contactId] = { ...contactsBucket.value[contactId], ...defaultData };
                contact.value = defaultContact({ id: contactId, ...contactsBucket.value[contactId] });
                resetDirty();
            }
            return;
        }

        unmount();
        groupId.value = prefillGroupId;

        if (defaultData) {
            contactsBucket.value[contactId] = { ...contactsBucket.value[contactId], ...defaultData };
        }

        if (contactsBucket.value[contactId]) {
            contact.value = defaultContact({
                id: contactId,
                ...contactsBucket.value[contactId],
            });
            resetDirty();
        } else {
            // If the contact doesn't exist in the bucket, initialize it with the given id.
            // This allows subsequent requests (e.g., in the message hub) to reference
            // the contact, even if the contact is no longer part of a group.
            contact.value = defaultContact({
                id: contactId,
            });
            resetDirty();
        }
    };

    const contact = ref(defaultContact());
    const { getDirty, resetDirty, isDirty } = useDirtyRef(contact);

    const contactNotFound = ref(false);
    const loading = ref(false);

    let getAbort;
    const mount = async (contactId, mountGroupId) => {
        getAbort?.abort();
        getAbort = new AbortController();

        loading.value = true;

        await nextTick();

        prefill(contactId, mountGroupId);

        if (!contactId) {
            loading.value = false;
            return;
        }

        try {
            contactNotFound.value = false;
            const response = groupId.value
                ? await groupContactsClient.groupContactGet(teamId, groupId.value, contactId, {
                      signal: getAbort.signal,
                  })
                : await contactsClient.contactGet(teamId, contactId, {
                      signal: getAbort.signal,
                  });
            contact.value = response.data;
            resetDirty();

            loading.value = false;
            contactsBucket.value[contactId] = { ...contact.value };
        } catch (e) {
            if (!axios.isCancel(e)) {
                loading.value = false;
            }

            contactNotFound.value = e?.response?.status === HTTP_NOT_FOUND;
            if (!contactNotFound.value) {
                catchContactError(e);
            }
        }
    };

    const unmount = (options) => {
        const loadingOption = options?.loading ?? true;

        contactNotFound.value = false;
        contact.value = defaultContact();
        resetDirty();

        loadingStickyPhoneNumber.value = loadingOption;
        stickyPhoneNumber.value = null;

        subscriptionsError.value = '';
        loadingSubscriptions.value = loadingOption;
        loadedSubscriptions.value = false;
        subscriptions.value = [];
        marketingStatus.value = 'unknown';
        transactionalStatus.value = 'unknown';

        loadingGroups.value = loadingOption;
        originalGroups.value = [];
        groups.value = [];

        loadingTags.value = loadingOption;
        originalTags.value = [];
        tags.value = [];

        loadingCustomAttributes.value = loadingOption;
        originalCustomAttributes.value = [];
        customAttributes.value = [];

        loadingAttributeSets.value = loadingOption;
        attributeSets.value = null;
        originalAttributeSets.value = null;
        selectedProfileId.value = null;

        loadingScheduledMessages.value = loadingOption;
        scheduledMessages.value = [];
        scheduledMessagesError.value = '';
        scheduledMessagesParams.value = {
            cursor: undefined,
            sort_field: 'send_at',
            sort_dir: 'asc',
            per_page: 4,
        };
    };

    const save = async () => {
        let createOrUpdateResponse;

        if (!isDirty()) {
            // No changes to save
            return contact.value.id;
        }

        const dataToSend = getDirty();
        if (groupId.value) {
            createOrUpdateResponse = await (contact.value.id
                ? groupContactsClient.groupContactUpdate(teamId, groupId.value, contact.value.id, dataToSend)
                : groupContactsClient.groupContactSave(teamId, groupId.value, dataToSend));
        } else {
            createOrUpdateResponse = await (contact.value.id
                ? contactsClient.contactUpdate(teamId, contact.value.id, dataToSend)
                : contactsClient.contactSave(teamId, dataToSend));
        }

        contact.value.id = createOrUpdateResponse.data.id;

        resetDirty();

        return contact.value.id;
    };

    const stickyPhoneNumber = ref(undefined);
    const loadingStickyPhoneNumber = ref(false);
    let stickyPhoneNumberAbort;
    const getStickyPhoneNumber = async () => {
        if (groupId.value) {
            return;
        }

        stickyPhoneNumberAbort?.abort();
        stickyPhoneNumberAbort = new AbortController();

        loadingStickyPhoneNumber.value = true;
        try {
            const response = await contactsClient.getStickyPhoneNumber(teamId, contact.value.id, {
                signal: stickyPhoneNumberAbort.signal,
            });
            stickyPhoneNumber.value = response.data;
        } catch (e) {
            if (e?.response?.status == HTTP_NOT_FOUND) {
                stickyPhoneNumber.value = undefined;
            } else {
                catchContactError(e);
            }
        } finally {
            loadingStickyPhoneNumber.value = false;
        }
    };

    const originalGroups = ref([]);
    const groups = ref([]);
    const loadingGroups = ref(false);

    let groupsAbort;
    const getGroups = async () => {
        if (!FEATURES.contact_groups) {
            return;
        }

        if (groupId.value) {
            return;
        }

        groupsAbort?.abort();
        groupsAbort = new AbortController();

        loadingGroups.value = true;
        try {
            const response = await contactsClient.listContactGroups(teamId, contact.value.id, {
                signal: groupsAbort.signal,
            });
            originalGroups.value = response.data.data.map((group) => ({ ...group }));
            groups.value = response.data.data.map((group) => ({ ...group }));
            loadingGroups.value = false;
        } catch (e) {
            catchContactError(e);
        }
    };
    const syncGroups = async () => {
        if (!FEATURES.contact_groups || groupId.value) {
            return;
        }

        const itemsToDelete = originalGroups.value.filter(
            (group) => !groups.value.map((g) => g.group_id).includes(group.group_id)
        );

        const itemsToCreate = groups.value.filter(
            (group) => !originalGroups.value.map((g) => g.group_id).includes(group.group_id)
        );

        await processWithConcurrency(itemsToDelete, MAX_CONCURRENT_REQUESTS, deleteGroup);
        await processWithConcurrency(itemsToCreate, MAX_CONCURRENT_REQUESTS, addGroup);

        originalGroups.value = groups.value.map((group) => ({ ...group }));
    };

    /**
     * @param {{ group_id: string }} addGroup
     */
    const addGroup = async (addGroup) => {
        if (!FEATURES.contact_groups) {
            return;
        }

        const response = await contactsClient.addContactGroup(teamId, contact.value.id, {
            group_id: addGroup.group_id,
        });

        if (!groups.value.find((group) => group.group_id === addGroup.group_id)) {
            groups.value.unshift(response.data);
        }
    };

    /**
     * @param {{ group_id: string }} deleteGroup
     */
    const deleteGroup = async (deleteGroup) => {
        if (!FEATURES.contact_groups) {
            return;
        }

        try {
            await contactsClient.deleteContactGroup(teamId, contact.value.id, deleteGroup.group_id);
            groups.value = groups.value.filter((group) => group.group_id !== deleteGroup.group_id);
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                throw e;
            }
        }
    };

    const subscriptions = ref([]);
    const loadingSubscriptions = ref(false);
    const loadedSubscriptions = ref(false);
    const marketingStatus = ref('unknown');
    const transactionalStatus = ref('unknown');

    const existingMarketingStatus = computed(
        () => subscriptions.value.find((subscription) => subscription.type === 'marketing')?.status || 'unknown'
    );
    const existingTransactionalStatus = computed(
        () => subscriptions.value.find((subscription) => subscription.type === 'transactional')?.status || 'unknown'
    );

    const subscriptionsError = ref('');
    let subscriptionsAbort;
    const getSubscriptions = async () => {
        subscriptionsAbort?.abort();
        subscriptionsAbort = new AbortController();

        loadingSubscriptions.value = true;
        try {
            const response = groupId.value
                ? await groupContactsClient.groupContactSubscriptionStatus(teamId, groupId.value, contact.value.id, {
                      signal: subscriptionsAbort.signal,
                  })
                : await contactsClient.getContactSubscriptionStatus(teamId, contact.value.id, {
                      signal: subscriptionsAbort.signal,
                  });
            subscriptions.value = response.data.data;

            marketingStatus.value = existingMarketingStatus.value;
            transactionalStatus.value = existingTransactionalStatus.value;
            loadingSubscriptions.value = false;
            loadedSubscriptions.value = true;
        } catch (e) {
            if (!axios.isCancel(e)) {
                subscriptionsError.value = catchRequestError(e);
                contactNotFound.value = e?.response?.status === HTTP_NOT_FOUND;
            }
        }
    };

    const canUnsubscribe = computed(
        () =>
            (existingMarketingStatus.value !== 'opt_out' || existingTransactionalStatus.value !== 'opt_out') &&
            loadedSubscriptions.value
    );
    const unsubscribe = async () => {
        VxSwal.confirmation('Unsubscribe Contact', 'Are you sure? There is no undo.', 'danger', async () => {
            try {
                marketingStatus.value = 'opt_out';
                transactionalStatus.value = 'opt_out';

                await saveSubscriptions();
            } catch (err) {
                getSubscriptions();
                throwSwalRequestError(err);
            }
        });
    };

    const saveSubscriptions = async () => {
        // only sends request when not unknown and when it is different from the existing subscriptions

        if (
            marketingStatus.value &&
            marketingStatus.value !== 'unknown' &&
            existingMarketingStatus.value !== marketingStatus.value
        ) {
            await contactsClient.updateContactSubscription(
                teamId,
                contact.value.id,
                'marketing',
                marketingStatus.value
            );

            if (existingMarketingStatus.value === 'unknown') {
                subscriptions.value.push({
                    type: 'marketing',
                    status: marketingStatus.value,
                });
            } else {
                subscriptions.value = subscriptions.value.map((subscription) => {
                    if (subscription.type === 'marketing') {
                        subscription.status = marketingStatus.value;
                    }
                    return subscription;
                });
            }
        }

        if (
            transactionalStatus.value &&
            transactionalStatus.value !== 'unknown' &&
            existingTransactionalStatus.value !== transactionalStatus.value
        ) {
            await contactsClient.updateContactSubscription(
                teamId,
                contact.value.id,
                'transactional',
                transactionalStatus.value
            );

            if (existingTransactionalStatus.value === 'unknown') {
                subscriptions.value.push({
                    type: 'transactional',
                    status: transactionalStatus.value,
                });
            } else {
                subscriptions.value = subscriptions.value.map((subscription) => {
                    if (subscription.type === 'transactional') {
                        subscription.status = transactionalStatus.value;
                    }
                    return subscription;
                });
            }
        }
    };

    const originalTags = ref([]);
    const tags = ref([]);
    const loadingTags = ref(false);

    let tagsAbort;
    const getTags = async () => {
        tagsAbort?.abort();
        tagsAbort = new AbortController();

        loadingTags.value = true;
        try {
            const response = groupId.value
                ? await groupContactsClient.groupContactTagsList(teamId, groupId.value, contact.value.id, {
                      signal: tagsAbort?.signal,
                  })
                : await contactsClient.contactTagsList(teamId, contact.value.id, { signal: tagsAbort?.signal });

            originalTags.value = response.data.data.map((tag) => tag.tag_name);
            tags.value = response.data.data.map((tag) => tag.tag_name);
            loadingTags.value = false;
        } catch (e) {
            if (!axios.isCancel(e)) {
                catchContactError(e);
            }
        }
    };

    const syncTags = async () => {
        const itemsToDelete = originalTags.value.filter((tagName) => !tags.value.includes(tagName));
        const itemsToCreate = tags.value.filter((tagName) => !originalTags.value.includes(tagName));

        await processWithConcurrency(itemsToDelete, MAX_CONCURRENT_REQUESTS, deleteTag);
        await processWithConcurrency(itemsToCreate, MAX_CONCURRENT_REQUESTS, addTag);

        originalTags.value = [...tags.value];
    };

    const addTag = async (tag) => {
        if (groupId.value) {
            await groupContactsClient.groupContactAddTag(teamId, groupId.value, contact.value.id, {
                tag_name: tag,
            });
        } else {
            await contactsClient.contactAddTag(teamId, contact.value.id, {
                tag_name: tag,
            });
        }

        if (!tags.value.find((tagName) => tagName === tag)) {
            tags.value.unshift(tag.toLowerCase());
        }
    };

    const deleteTag = async (tag) => {
        try {
            if (groupId.value) {
                await groupContactsClient.groupContactDeleteTag(teamId, groupId.value, contact.value.id, tag);
            } else {
                await contactsClient.contactDeleteTag(teamId, contact.value.id, tag);
            }
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                throw e;
            }
        }

        tags.value = tags.value.filter((existingTag) => existingTag !== tag);
    };

    const originalCustomAttributes = ref([]);
    const customAttributes = ref([]);
    const loadingCustomAttributes = ref(false);

    let customAttributesAbort;
    const getCustomAttributes = async () => {
        customAttributesAbort?.abort();
        customAttributesAbort = new AbortController();

        loadingCustomAttributes.value = true;
        try {
            const response = groupId.value
                ? await groupContactsClient.groupContactCustomAttributesList(teamId, groupId.value, contact.value.id, {
                      signal: customAttributesAbort.signal,
                  })
                : await contactsClient.contactCustomAttributesList(teamId, contact.value.id, {
                      signal: customAttributesAbort.signal,
                  });

            originalCustomAttributes.value = response.data.data.map((attribute) => ({ ...attribute }));
            customAttributes.value = response.data.data.map((attribute) => ({ ...attribute }));
            loadingCustomAttributes.value = false;
        } catch (e) {
            catchContactError(e);
        }
    };

    /**
     * @param {{ key: string, value: string }} customAttribute
     */
    const upsertCustomAttribute = async (customAttribute) => {
        const response = groupId.value
            ? await groupContactsClient.groupContactCustomAttributesUpsert(
                  teamId,
                  groupId.value,
                  contact.value.id,
                  customAttribute
              )
            : await contactsClient.upsertContactsCustomAttribute(teamId, contact.value.id, customAttribute);

        const responseCustomAttribute = response.data;

        const index = customAttributes.value.findIndex(
            (attribute) =>
                attribute.key === responseCustomAttribute.key && attribute.value === responseCustomAttribute.value
        );

        if (index !== -1) {
            customAttributes.value[index] = { ...customAttributes.value[index], ...responseCustomAttribute };
        } else {
            customAttributes.value.unshift(responseCustomAttribute);
        }
    };

    const deleteCustomAttribute = async (customAttribute) => {
        try {
            if (groupId.value) {
                await groupContactsClient.groupContactCustomAttributesDelete(
                    teamId,
                    groupId.value,
                    contact.value.id,
                    customAttribute.id
                );
            } else {
                await contactsClient.deleteContactsCustomAttribute(teamId, contact.value.id, customAttribute.id);
            }
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                throw e;
            }
        }

        customAttributes.value = customAttributes.value.filter((item) => item.key != customAttribute.key);
    };

    const syncCustomAttributes = async () => {
        const { upserts, deletes } = diffCustomAttributes(originalCustomAttributes.value, customAttributes.value);

        await processWithConcurrency(deletes, MAX_CONCURRENT_REQUESTS, deleteCustomAttribute);
        await processWithConcurrency(upserts, MAX_CONCURRENT_REQUESTS, upsertCustomAttribute);

        originalCustomAttributes.value = customAttributes.value.map((attribute) => ({ ...attribute }));
    };

    const attributeSets = ref();
    const originalAttributeSets = ref();
    const loadingAttributeSets = ref(false);
    const selectedProfileId = ref();

    const seedAttributeSets = () => {
        attributeSets.value = groupId.value
            ? {
                  corporate: { ...emptyProfile(), editable: false },
                  groups: [
                      {
                          group_id: groupId.value,
                          group_name: currentGroup.value?.group_id === groupId.value ? currentGroup.value.name : null,
                          is_preferred: true,
                          ...emptyProfile(),
                          editable: true,
                      },
                  ],
              }
            : { corporate: { ...emptyProfile(), editable: true }, groups: [] };
        originalAttributeSets.value = JSON.parse(JSON.stringify(attributeSets.value));
        selectedProfileId.value = groupId.value ?? 'corporate';
    };

    let attributeSetsAbort;
    const getAttributeSets = async () => {
        if (!FEATURES.local_attributes || !FEATURES.contact_groups || !contact.value.id) {
            return;
        }

        attributeSetsAbort?.abort();
        attributeSetsAbort = new AbortController();

        loadingAttributeSets.value = true;
        try {
            const response = groupId.value
                ? await groupContactsClient.groupContactAttributeSetsGet(teamId, groupId.value, contact.value.id, {
                      signal: attributeSetsAbort.signal,
                  })
                : await contactsClient.contactAttributeSetsGet(teamId, contact.value.id, {
                      signal: attributeSetsAbort.signal,
                  });

            attributeSets.value = response.data;
            originalAttributeSets.value = JSON.parse(JSON.stringify(response.data));

            const profileIds = ['corporate', ...response.data.groups.map((profile) => profile.group_id)];
            if (!profileIds.includes(selectedProfileId.value)) {
                selectedProfileId.value = profileIds.includes(groupId.value) ? groupId.value : 'corporate';
            }

            loadingAttributeSets.value = false;
        } catch (e) {
            catchContactError(e);
        }
    };

    const attributeSetsWrite = () => {
        const { corporate, groups } = attributeSets.value;
        const original = originalAttributeSets.value;
        const write = {};

        const corporateWrite = corporate.editable ? profileWrite(corporate, original.corporate) : null;
        if (corporateWrite) {
            write.corporate = corporateWrite;
        }

        const groupWrites = [];
        for (const profile of groups) {
            if (!profile.editable) {
                continue;
            }

            const originalProfile = original.groups.find((p) => p.group_id === profile.group_id);
            const groupWrite = profileWrite(profile, originalProfile);
            if (groupWrite) {
                groupWrites.push({ group_id: profile.group_id, ...groupWrite });
            }
        }
        if (groupWrites.length) {
            write.groups = groupWrites;
        }

        return write;
    };

    const saveAttributeSets = async () => {
        if (!attributeSets.value) {
            return;
        }

        const write = attributeSetsWrite();
        if (!write.corporate && !write.groups) {
            return;
        }

        attributeSetsAbort?.abort();
        loadingAttributeSets.value = false;

        const response = groupId.value
            ? await groupContactsClient.groupContactAttributeSetsUpdate(teamId, groupId.value, contact.value.id, write)
            : await contactsClient.contactAttributeSetsUpdate(teamId, contact.value.id, write);

        attributeSets.value = response.data;
        originalAttributeSets.value = JSON.parse(JSON.stringify(response.data));
    };

    const fullName = computed(() => {
        const fullName = `${contact.value.first_name || ''} ${contact.value.last_name || ''}`.trim();
        return fullName || formatPhoneNumber(contact.value.phone);
    });

    let scheduledMessagesAbort;
    const {
        fetchRecords: fetchScheduledMessages,
        fetchParams: scheduledMessagesParams,
        loading: loadingScheduledMessages,
        list: scheduledMessages,
        error: scheduledMessagesError,
    } = useCursorPagination();

    const getScheduledMessages = async () => {
        scheduledMessagesAbort?.abort();
        scheduledMessagesAbort = new AbortController();

        fetchScheduledMessages(
            scheduledMessagesClient.getContactScheduledMessages(
                { teamId, groupId: groupId.value },
                contact.value.id,
                { ...scheduledMessagesParams.value },
                {
                    signal: scheduledMessagesAbort.signal,
                }
            )
        );
    };

    return {
        groupId,

        contact,
        contactNotFound,
        loading,

        fullName,

        isCached,
        prefill,

        mount,
        unmount,
        save,

        getStickyPhoneNumber,
        loadingStickyPhoneNumber,
        stickyPhoneNumber,

        getGroups,
        loadingGroups,
        groups,
        syncGroups,
        deleteGroup,
        addGroup,

        getSubscriptions,
        canUnsubscribe,
        unsubscribe,
        saveSubscriptions,
        loadingSubscriptions,
        subscriptions,
        subscriptionsError,

        marketingStatus,
        existingMarketingStatus,

        transactionalStatus,
        existingTransactionalStatus,

        getTags,
        loadingTags,
        tags,
        syncTags,
        addTag,
        deleteTag,

        getCustomAttributes,
        loadingCustomAttributes,
        customAttributes,
        syncCustomAttributes,
        upsertCustomAttribute,
        deleteCustomAttribute,

        getAttributeSets,
        loadingAttributeSets,
        attributeSets,
        selectedProfileId,
        saveAttributeSets,

        getScheduledMessages,
        scheduledMessagesParams,
        loadingScheduledMessages,
        scheduledMessages,
        scheduledMessagesError,
    };
});
